import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Box, 
  CssBaseline, 
  Divider, 
  Drawer, 
  IconButton, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  Toolbar, 
  Typography, 
  Button,
  Tooltip
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  Dashboard as DashboardIcon, 
  Inventory as InventoryIcon, 
  LocalShipping as LocalShippingIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Tag as TagIcon,
  Assignment as TasksIcon,
  Storefront as StorefrontIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { canAccessPath, canViewProductLinks, isCounterRole } from '../config/permissions';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InsightsIcon from '@mui/icons-material/Insights';
import StyleIcon from '@mui/icons-material/Style';
import PeopleIcon from '@mui/icons-material/People';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import StoreMallDirectoryOutlinedIcon from '@mui/icons-material/StoreMallDirectoryOutlined';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ChecklistIcon from '@mui/icons-material/Checklist';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import RadarOutlinedIcon from '@mui/icons-material/RadarOutlined';

const drawerWidth = 240;
const collapsedDrawerWidth = 60;

const OWNER_USER_ID = 1;

// Tabs only visible to the owner (user ID 1)
const OWNER_ONLY_PATHS = new Set([
  '/owner-board',
  '/markdown-patrol',
  '/stores',
  '/analytics',
  '/retention',
  '/shopify-invoice',
  '/tag-management',
  '/performance',
  '/inventory-health',
]);

const Layout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout } = useAuth();

  const isOwner = currentUser?.id === OWNER_USER_ID;
  // NOKE 門市人員: sees the two NOKE screens and the logout button, nothing else.
  const isCounter = isCounterRole(currentUser?.role);

  const handleLogout = async () => {
    await logout();
    navigate('/auth');
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopToggle = () => {
    setDesktopCollapsed(!desktopCollapsed);
  };

  const allMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Receiving', icon: <InventoryIcon />, path: '/receiving' },
    { text: '新款雷達', icon: <RadarOutlinedIcon />, path: '/scout' },
    { text: 'Stores', icon: <StorefrontIcon />, path: '/stores' },
    { text: 'Shipping Orders', icon: <LocalShippingIcon />, path: '/shipping-orders' },
    // Orders (/orders) and Storefront Orders (/storefront-orders) hidden from nav —
    // superseded by Receiving, which unifies Shopee + 官網 orders. Routes still work
    // if reached by direct URL.
    { text: '補寄訂單', icon: <AddBoxOutlinedIcon />, path: '/manual-order' },
    { text: '代購訂單', icon: <ShoppingBagOutlinedIcon />, path: '/daigou-orders' },
    { text: '寄錯紀錄', icon: <ReportProblemOutlinedIcon />, path: '/mis-shipments' },
    { text: 'Products', icon: <InventoryIcon />, path: '/products' },
    { text: 'Return', icon: <TagIcon />, path: '/return-management' },
    { text: 'Outlet Manager', icon: <InventoryIcon />, path: '/admin-outlet' },
    { text: 'NOKE 專櫃', icon: <StoreMallDirectoryOutlinedIcon />, path: '/noke' },
    { text: 'NOKE 收銀', icon: <PointOfSaleOutlinedIcon />, path: '/noke/pos' },
    { text: 'NOKE 打卡', icon: <AccessTimeOutlinedIcon />, path: '/noke/clock' },
    { text: 'NOKE 活動折扣', icon: <LocalOfferOutlinedIcon />, path: '/noke/campaigns' },
    { text: 'Threads 海巡', icon: <ForumOutlinedIcon />, path: '/threads-patrol' },
    { text: 'Fukuro', icon: <TasksIcon />, path: '/fukubukuro' },
    { text: '任務看板', icon: <ChecklistIcon />, path: '/tasks' },
    { text: '每日看板', icon: <TodayOutlinedIcon />, path: '/owner-board' },
    { text: '降價巡邏', icon: <TrendingDownIcon />, path: '/markdown-patrol' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
    { text: 'Performance', path: '/performance', icon: <AssessmentIcon /> },
    { text: 'Tag Management', icon: <TagIcon />, path: '/tag-management' },
    { text: 'Invoice', icon: <TasksIcon />, path: '/shopify-invoice' },
    { text: 'Looks', icon: <StyleIcon />, path: '/looks' },
    { text: 'Reviews', icon: <RateReviewOutlinedIcon />, path: '/admin/reviews' },
    { text: 'Credits', icon: <AccountBalanceWalletOutlinedIcon />, path: '/admin/credits' },
    { text: '滿額贈', icon: <CardGiftcardOutlinedIcon />, path: '/admin/gift-promos' },
    { text: 'Analytics', icon: <InsightsIcon />, path: '/analytics' },
    { text: 'Retention', icon: <PeopleIcon />, path: '/retention' },
    { text: 'Inventory Health', icon: <WarehouseOutlinedIcon />, path: '/inventory-health' },
  ];

  const menuItems = allMenuItems.filter(item =>
    canAccessPath(currentUser?.role, item.path) &&
    (isOwner || !OWNER_ONLY_PATHS.has(item.path)) &&
    (item.path !== '/scout' || canViewProductLinks(currentUser?.id))
  );

  const currentDrawerWidth = desktopCollapsed ? collapsedDrawerWidth : drawerWidth;

  const drawer = (collapsed = false) => (
    <div>
      <Toolbar>
        {!collapsed && (
          <Typography variant="h6" noWrap component="div">
            Order Management
          </Typography>
        )}
        {collapsed && (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Typography variant="h6" noWrap component="div" sx={{ fontSize: '1rem' }}>
              OM
            </Typography>
          </Box>
        )}
      </Toolbar>
      <Divider />
      
      {/* Desktop toggle button */}
      <Box sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'center', py: 1 }}>
        <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
          <IconButton 
            onClick={handleDesktopToggle}
            size="small"
            sx={{ 
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.08)',
              }
            }}
          >
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>
      </Box>
      
      <Divider />
      
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <Tooltip 
              title={collapsed ? item.text : ''} 
              placement="right"
              arrow
            >
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => navigate(item.path)}
                sx={{
                  minHeight: 48,
                  justifyContent: collapsed ? 'center' : 'initial',
                  px: 2.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: collapsed ? 0 : 3,
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!collapsed && <ListItemText primary={item.text} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        {/* 門市人員 have no Settings page, which is where the app's only other
            logout lives — without this they could not sign out at all. */}
        {isCounter && (
          <ListItem disablePadding>
            <Tooltip title={collapsed ? '登出' : ''} placement="right" arrow>
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  minHeight: 48,
                  justifyContent: collapsed ? 'center' : 'initial',
                  px: 2.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: collapsed ? 0 : 3,
                    justifyContent: 'center',
                  }}
                >
                  <LogoutIcon />
                </ListItemIcon>
                {!collapsed && <ListItemText primary="登出" />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {/* Mobile-only top bar */}
      <AppBar
        position="fixed"
        sx={{
          display: { xs: 'block', sm: 'none' },
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {(menuItems.find(item => item.path === location.pathname) ||
              menuItems.find(item => item.path !== '/' && location.pathname.startsWith(`${item.path}/`)))?.text || 'Dashboard'}
          </Typography>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ 
          width: { sm: currentDrawerWidth }, 
          flexShrink: { sm: 0 },
          transition: (theme) => theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
        aria-label="mailbox folders"
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth 
            },
          }}
        >
          {drawer(false)}
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: currentDrawerWidth,
              transition: (theme) => theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawer(desktopCollapsed)}
        </Drawer>
      </Box>
      
      {/* Main content */}
      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          p: 3, 
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          marginTop: { xs: '64px', sm: 0 }, // Top margin only for mobile
          transition: (theme) => theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;